import { breakRegex, MILEAGE_LIMIT_FIELD, MILEAGE_LIMIT_GROUP } from '@/configs/constants';
import { MileageLimitSetting, MileageLimitSettingCvItem, OtherSettingItem } from '@/types/system-setting';
import { useTranslation } from 'react-i18next';
import { OptionComp } from '../OptionComp';
import { formatCurrency } from '@/utils/formatNumber';
import { useEffect, useRef, useState } from 'react';
import ScrollTableGuide from '@/components/general/ScrollTableGuide';
import { cn } from '@/utils/utils';
import insertBreakOpportunities from '@/components/general/FieldNameConvert';

interface MileageLimitCompProps {
    mileageLimitSetting: MileageLimitSetting,
    isOnPopUp?: boolean
}
export default function MileageLimitComp({ mileageLimitSetting, isOnPopUp = false }: MileageLimitCompProps) {
    // hook
    const { t } = useTranslation();

    // const
    const { variable, fixed } = mileageLimitSetting;

    let var_capacity125cc = variable.capacity125cc?.length > 0 ? variable.capacity125cc[0] : {} as OtherSettingItem;
    let var_capacity126cc = variable.capacity126cc?.length > 0 ? variable.capacity126cc[0] : {} as OtherSettingItem;
    let fixed_capacity125cc = fixed.capacity125cc?.length > 0 ? fixed.capacity125cc[0] : {} as OtherSettingItem;
    let fixed_capacity126cc = fixed.capacity126cc?.length > 0 ? fixed.capacity126cc[0] : {} as OtherSettingItem;

    let variableItem: MileageLimitSettingCvItem = {
        capacity125cc: {
            over24hKm: var_capacity125cc.over24hKm ?? '0',
            over48hKm: var_capacity125cc.over48hKm ?? '0',
            over72hKm: var_capacity125cc.over72hKm ?? '0',
            over96hKm: var_capacity125cc.over96hKm ?? '0',
            price: var_capacity125cc.price ?? '0'
        },
        capacity126cc: {
            over24hKm: var_capacity126cc.over24hKm ?? '0',
            over48hKm: var_capacity126cc.over48hKm ?? '0',
            over72hKm: var_capacity126cc.over72hKm ?? '0',
            over96hKm: var_capacity126cc.over96hKm ?? '0',
            price: var_capacity126cc.price ?? '0'
        }
    };

    let fixedItem: MileageLimitSettingCvItem = {
        capacity125cc: {
            price: fixed_capacity125cc.price ?? '0'
        },
        capacity126cc: {
            price: fixed_capacity126cc.price ?? '0'
        }
    };
    return (
        <div className='flex flex-col gap-5'>
            {
                !isOnPopUp ?
                    (
                        <OptionComp
                            option={{
                                title: t('price_list.title_section.mileage_limit'),
                                textDecoration: t('price_list.text_decoration.mileage_limit'),
                                descriptionList: [t('sentences.mileage_limit_des_1'), t('sentences.mileage_limit_des_2')],
                                contentComp: <ContentOfMileageSetting variable={variableItem} fixed={fixedItem} />,
                                specialNote: [t('sentences.mileage_limit_var_note_1'), t('sentences.price_contain_tax')]
                            }}
                        />
                    ) :
                    (
                        <OptionComp
                            option={{
                                title: t('price_list.title_section.mileage_limit'),
                                textDecoration: '',
                                descriptionList: [t('sentences.mileage_limit_des_2')],
                                contentComp: <ContentOfMileageSetting variable={variableItem} fixed={fixedItem} />,
                                specialNote: [t('sentences.mileage_limit_var_note_1'), t('sentences.price_contain_tax')]
                            }}
                        />
                    )
            }

            <div className='flex flex-col gap-5'>
                <div className='md:px-10 px-5 py-5 bg-subtle rounded-[5px] xsm:leading-[30px] leading-[20px] flex'>
                    <p>例</p>
                    <p>：</p>
                    <div className='flex flex-col'>
                        <p className='break-words'>{t('sentences.mileage_limit_var_sample_1')}</p>
                        <p className='break-words'>{t('sentences.mileage_limit_var_sample_2')}</p>
                    </div>
                </div>
                <div className='flex flex-col items-center justify-center gap-5'>
                    <p className='text-fs-mid12 xsm:text-fs-mid13 xsm:leading-[24px] leading-[20px] text-black-6 text-center'>{t('sentences.mileage_limit_fixed_des_1')}</p>
                    <div className='w-[520px] max-w-full border-border-gray2 border-t'>
                        {
                            Object.keys(MILEAGE_LIMIT_GROUP).map((key, idx) => {
                                let checkExistBreakSymbol = breakRegex.test(MILEAGE_LIMIT_GROUP[key].label);
                                return (
                                    <div key={key + idx} className='grid grid-cols-2'>
                                        <div
                                            className={cn(
                                                'bg-subtle font-medium common-item break-on-wbr flex items-center justify-end border-b border-x border-border-gray2 text-right'
                                            )}
                                        >
                                            {
                                                checkExistBreakSymbol ?
                                                    insertBreakOpportunities({ text: MILEAGE_LIMIT_GROUP[key].label }) :
                                                    MILEAGE_LIMIT_GROUP[key].label
                                            }
                                        </div>
                                        <div className='common-item font-normal flex flex-col items-center justify-center text-center border-r border-b border-border-gray2'>
                                            <p>
                                                {formatCurrency(fixedItem[key].price)}
                                                {`円/${MILEAGE_LIMIT_GROUP[key].km}km`}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                    <div className='text-fs-mid12 xsm:text-fs-mid13 xsm:leading-[24px] leading-[20px] text-black-6 flex flex-col items-center justify-center text-center'>
                        <p>{t('sentences.mileage_limit_fixed_note_1')}</p>
                        <p>{t('sentences.mileage_limit_fixed_note_2')}</p>
                        <p>{t('sentences.mileage_limit_fixed_note_3')}</p>
                    </div>
                </div>
            </div>

        </div>
    );
}

interface ContentOfMileageSettingProps {
    variable: MileageLimitSettingCvItem,
    fixed: MileageLimitSettingCvItem
}
const ContentOfMileageSetting = ({ variable, fixed }: ContentOfMileageSettingProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showHint, setShowHint] = useState<boolean>(false);
    const onFirstScroll = useRef<boolean>(true);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const checkScrollable = () => setShowHint(el.scrollWidth > el.clientWidth);
        requestAnimationFrame(checkScrollable);

        const handleScroll = () => {
            if (el.scrollLeft > 0 && onFirstScroll.current) {
                onFirstScroll.current = false;
                setShowHint(false);
            }
        };
        el.addEventListener('scroll', handleScroll);
        return () => {
            el.removeEventListener('scroll', handleScroll);
        };
    }, [variable]);

    return (
        <div className='relative'>
            <div className='overflow-x-auto max-w-full' ref={scrollRef}>
                <ScrollTableGuide showHint={showHint && onFirstScroll.current} />
                <div className='flex point-900:grid point-900:grid-cols-7 max-w-full'>
                    <div className='point-900:col-span-2 ex-common-item field-item-cus border-x border-b border-border-gray2 bg-subtle'></div>
                    {Object.keys(MILEAGE_LIMIT_FIELD).map((key, index) => {
                        return (
                            <div
                                key={index}
                                className='bg-subtle ex-common-item font-medium flex flex-col items-center justify-center text-center border-r border-b border-border-gray2'
                            >
                                <p>{MILEAGE_LIMIT_FIELD[key].label}</p>
                                {
                                    MILEAGE_LIMIT_FIELD.price.field !== MILEAGE_LIMIT_FIELD[key].field && <p>基準走行距離</p>
                                }
                            </div>
                        );
                    })}
                </div>
                {
                    Object.keys(MILEAGE_LIMIT_GROUP).map((key, idx) => {
                        let checkExistBreakSymbol = breakRegex.test(MILEAGE_LIMIT_GROUP[key].label);

                        return (
                            <div key={key + idx} className='flex point-900:grid point-900:grid-cols-7'>
                                <div
                                    className={cn(
                                        'bg-subtle point-900:col-span-2 field-item-cus font-medium flex break-on-wbr items-center justify-end ex-common-item border-b border-x border-border-gray2 text-right'

                                    )}
                                >
                                    <p>{checkExistBreakSymbol ? insertBreakOpportunities({ text: MILEAGE_LIMIT_GROUP[key].label }) : MILEAGE_LIMIT_GROUP[key].label}</p>
                                </div>
                                {Object.keys(MILEAGE_LIMIT_FIELD).map((field, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className='ex-common-item col-span-1 font-normal flex flex-col items-center justify-center text-center border-r border-b border-border-gray2'
                                        >
                                            <p>
                                                {formatCurrency(variable[key][MILEAGE_LIMIT_FIELD[field].field])}
                                                {MILEAGE_LIMIT_FIELD.price.field !== MILEAGE_LIMIT_FIELD[field].field ? 'km' : `円/${MILEAGE_LIMIT_GROUP[key].km}km`}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })
                }
            </div>
        </div>

    );
};
